import React, { useRef, useState } from "react";
import { FileText, Upload } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";
import { useToasts } from "../hooks/useToasts";
import Button from "./Button";
import "./PdfImportPanel.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const PDF_IMPORT_MESSAGE =
  "PDF import speeds up lesson creation, but conversion is not always perfect. Results depend on the PDF structure, and scanned or image-based pages may not extract correctly. Please review the content before saving.";

const PDF_HYPHEN_CHARS = "-\u00ad\u2010\u2011\u2012\u2013\u2014";
const ROMANIAN_HYPHEN_SUFFIXES = new Set([
  "a",
  "ai",
  "al",
  "ale",
  "am",
  "ar",
  "au",
  "l",
  "le",
  "li",
  "mi",
  "na",
  "ne",
  "o",
  "or",
  "s",
  "sa",
  "se",
  "si",
  "te",
  "ti",
  "u",
  "ui",
  "ul",
  "ului",
  "un",
  "unei",
  "unor",
]);
const ROMANIAN_HYPHEN_PREFIXES = new Set([
  "intr",
  "dintr",
  "printr",
  "tr",
  "n",
  "s",
  "l",
  "m",
  "mi",
  "ti",
  "v",
]);
const PRESERVED_HYPHEN_PREFIXES = new Set([
  ...ROMANIAN_HYPHEN_PREFIXES,
  "au",
  "ci",
  "contre",
  "demi",
  "elle",
  "grand",
  "haut",
  "moi",
  "nous",
  "sous",
  "sur",
  "t",
  "toi",
  "vous",
]);
const PRESERVED_HYPHEN_SUFFIXES = new Set([
  ...ROMANIAN_HYPHEN_SUFFIXES,
  "ci",
  "delà",
  "dessous",
  "dessus",
  "elle",
  "elles",
  "en",
  "eux",
  "il",
  "ils",
  "là",
  "leur",
  "leurs",
  "même",
  "mêmes",
  "moi",
  "nous",
  "on",
  "toi",
  "vous",
  "y",
]);
const WORD_PART_REGEX = /[A-Za-zÀ-ÖØ-öø-ÿĂÂÎȘŞȚŢăâîșşțţ]+/g;

const normalizePdfSpacingArtifacts = (value) =>
  value
    .replace(/[\u200b-\u200d\u2060\ufeff]/g, "")
    .replace(/\u00ad/g, "-");

const getTrailingWordPart = (value) => {
  const matches = value.match(WORD_PART_REGEX);
  return matches?.[matches.length - 1] || "";
};

const getLeadingWordPart = (value) => {
  const matches = value.match(WORD_PART_REGEX);
  return matches?.[0] || "";
};

const shouldPreserveHyphenBetweenParts = (leftPart, rightPart) => {
  const normalizedLeft = leftPart.toLowerCase();
  const normalizedRight = rightPart.toLowerCase();

  if (!normalizedLeft || !normalizedRight) {
    return true;
  }

  if (
    PRESERVED_HYPHEN_PREFIXES.has(normalizedLeft) ||
    PRESERVED_HYPHEN_SUFFIXES.has(normalizedRight)
  ) {
    return true;
  }

  if (normalizedLeft.length <= 2 || normalizedRight.length <= 2) {
    return true;
  }

  if (/^[A-ZĂÂÎȘŞȚŢÀ-ÖØ-Þ]/.test(leftPart) || /^[A-ZĂÂÎȘŞȚŢÀ-ÖØ-Þ]/.test(rightPart)) {
    return true;
  }

  return false;
};

const mergeHyphenatedFragments = (leftText, rightText) => {
  if (!leftText.endsWith("-")) {
    return `${leftText} ${rightText}`;
  }

  const leftPart = getTrailingWordPart(leftText.slice(0, -1));
  const rightPart = getLeadingWordPart(rightText);

  if (shouldPreserveHyphenBetweenParts(leftPart, rightPart)) {
    return `${leftText}${rightText}`;
  }

  return `${leftText.slice(0, -1)}${rightText}`;
};

const normalizeBrokenRomanianWords = (value) => {
  let normalizedValue = normalizePdfSpacingArtifacts(value);
  let previousValue = "";

  while (normalizedValue !== previousValue) {
    previousValue = normalizedValue;
    normalizedValue = normalizedValue.replace(
      /([A-Za-zĂÂÎȘŞȚŢăâîșşțţ]+)\s+([ĂÂÎȘŞȚŢăâîșşțţ])\s+([A-Za-zĂÂÎȘŞȚŢăâîșşțţ]+)/g,
      "$1$2$3",
    );
  }

  return normalizedValue;
};

const normalizeExtractedPdfText = (value) =>
  normalizeBrokenRomanianWords(
    normalizePdfSpacingArtifacts(value).replace(/\s{2,}/g, " ").trim(),
  );

const formatEnumeratedOutline = (value) =>
  value
    .replace(/\s+(\d+\.\d+\.)\s+/g, "\n$1 ")
    .replace(
      /([a-zăâîșşțţ])\s+([1-9]\d?)\s+([A-ZĂÂÎȘŞȚŢ])/g,
      "$1\n\n$2 $3",
    )
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const formatBulletOutline = (value) =>
  value
    .replace(/\s+([•◦▪▫‣▶►▸▹➜➤➤➢])\s*/g, "\n$1 ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const cleanPdfText = (value) =>
  normalizeExtractedPdfText(
    value
      .replace(/\u0000/g, "")
      .replace(/\s+/g, " ")
      .trim(),
  );

const cleanTableOfContentsLine = (value) =>
  value
    .replace(/\s*(?:\.\s*){4,}(?=\s*\d+\s*$)/g, " ")
    .replace(/\s+\d+\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const isTableOfContentsLine = (value) =>
  /\s(?:\.\s*){4,}\s*\d+\s*$/.test(value) || /\.{4,}\s*\d+\s*$/.test(value);

const joinLineFragments = (fragments) =>
  fragments.reduce((lineText, fragment, index) => {
    if (index === 0) {
      return fragment;
    }

    if (!fragment) {
      return lineText;
    }

    if (lineText.endsWith("-")) {
      return mergeHyphenatedFragments(lineText, fragment);
    }

    if (/^[,.;:!?%)\]]/.test(fragment)) {
      return `${lineText}${fragment}`;
    }

    if (/^['"]/.test(fragment) && /[([{]$/.test(lineText)) {
      return `${lineText}${fragment}`;
    }

    return `${lineText} ${fragment}`;
  }, "");

const getMedian = (values) => {
  if (!values.length) {
    return 0;
  }

  const sortedValues = [...values].sort((a, b) => a - b);
  const middleIndex = Math.floor(sortedValues.length / 2);

  if (sortedValues.length % 2 === 0) {
    return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
  }

  return sortedValues[middleIndex];
};

const buildParagraphTextFromPage = (textContent) => {
  const positionedItems = textContent.items
    .map((item) => {
      if (!("str" in item)) {
        return null;
      }

      const text = cleanPdfText(item.str);
      if (!text) {
        return null;
      }

      const transform = Array.isArray(item.transform) ? item.transform : [];
      const x = typeof transform[4] === "number" ? transform[4] : 0;
      const y = typeof transform[5] === "number" ? transform[5] : 0;
      const height =
        typeof item.height === "number" && item.height > 0
          ? item.height
          : Math.abs(typeof transform[3] === "number" ? transform[3] : 0) || 12;

      return {
        text,
        x,
        y,
        height,
      };
    })
    .filter(Boolean);

  if (!positionedItems.length) {
    return "";
  }

  const medianHeight = getMedian(
    positionedItems.map((item) => item.height).filter((value) => value > 0),
  );
  const lineThreshold = Math.max(2.5, medianHeight * 0.45);

  const sortedItems = [...positionedItems].sort((left, right) => {
    if (Math.abs(right.y - left.y) > lineThreshold) {
      return right.y - left.y;
    }

    return left.x - right.x;
  });

  const lines = [];

  sortedItems.forEach((item) => {
    const currentLine = lines[lines.length - 1];

    if (!currentLine || Math.abs(currentLine.y - item.y) > lineThreshold) {
      lines.push({
        y: item.y,
        height: item.height,
        fragments: [item],
      });
      return;
    }

    currentLine.fragments.push(item);
    currentLine.height = Math.max(currentLine.height, item.height);
    currentLine.y = (currentLine.y + item.y) / 2;
  });

  const normalizedLines = lines
    .map((line) => {
      const sortedFragments = [...line.fragments].sort((left, right) => left.x - right.x);
      const text = joinLineFragments(sortedFragments.map((fragment) => fragment.text));
      const cleanedLineText = cleanTableOfContentsLine(text);

      return {
        text: cleanPdfText(cleanedLineText),
        preserveAsStandaloneLine: isTableOfContentsLine(text),
        y: line.y,
        height: line.height,
      };
    })
    .filter((line) => line.text);

  if (!normalizedLines.length) {
    return "";
  }

  const lineGaps = [];
  for (let index = 1; index < normalizedLines.length; index += 1) {
    const gap = normalizedLines[index - 1].y - normalizedLines[index].y;
    if (gap > 0) {
      lineGaps.push(gap);
    }
  }

  const medianLineGap = getMedian(lineGaps);
  const paragraphThreshold = Math.max(
    medianHeight * 1.35,
    medianLineGap > 0 ? medianLineGap * 1.6 : medianHeight * 1.8,
  );

  const paragraphs = [];
  let currentParagraph = [];
  let currentParagraphStandalone = false;

  normalizedLines.forEach((line, index) => {
    if (index === 0) {
      currentParagraph.push(line.text);
      currentParagraphStandalone = line.preserveAsStandaloneLine;
      return;
    }

    const previousLine = normalizedLines[index - 1];
    const gap = previousLine.y - line.y;
    const startsNewParagraph =
      gap > paragraphThreshold ||
      previousLine.preserveAsStandaloneLine ||
      line.preserveAsStandaloneLine;

    if (startsNewParagraph && currentParagraph.length) {
      paragraphs.push({
        text: currentParagraph.reduce((result, lineText) => {
          if (!result) {
            return lineText;
          }

          if (result.endsWith("-")) {
            return mergeHyphenatedFragments(result, lineText);
          }

          return `${result} ${lineText}`;
        }, ""),
        standalone: currentParagraphStandalone,
      });
      currentParagraph = [];
    }

    if (!currentParagraph.length) {
      currentParagraphStandalone = line.preserveAsStandaloneLine;
    }

    currentParagraph.push(line.text);
  });

  if (currentParagraph.length) {
    paragraphs.push({
      text: currentParagraph.reduce((result, lineText) => {
        if (!result) {
          return lineText;
        }

        if (result.endsWith("-")) {
          return mergeHyphenatedFragments(result, lineText);
        }

        return `${result} ${lineText}`;
      }, ""),
      standalone: currentParagraphStandalone,
    });
  }

  const cleanedParagraphs = paragraphs
    .map((paragraph) => ({
      ...paragraph,
      text: normalizeExtractedPdfText(
        formatBulletOutline(
          formatEnumeratedOutline(
            paragraph.text.replace(/\s{2,}/g, " ").trim(),
          ),
        ),
      ),
    }))
    .filter((paragraph) => paragraph.text);

  return cleanedParagraphs.reduce((result, paragraph, index) => {
    if (index === 0) {
      return paragraph.text;
    }

    const previousParagraph = cleanedParagraphs[index - 1];
    const separator =
      previousParagraph.standalone && paragraph.standalone ? "\n" : "\n\n";

    return `${result}${separator}${paragraph.text}`;
  }, "");
};

const PdfImportPanel = ({
  currentText = "",
  onImportText,
  maxLength,
  placement = "top",
  triggerVariant = "default",
}) => {
  const { addToast } = useToasts();
  const pdfInputRef = useRef(null);
  const pdfDataRef = useRef(null);
  const [pdfFileName, setPdfFileName] = useState("");
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfPagesInput, setPdfPagesInput] = useState("");
  const [pdfPanelOpen, setPdfPanelOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const getPdfDocumentOptions = (data) => ({
    data,
    useWorkerFetch: false,
    useWasm: false,
    isOffscreenCanvasSupported: false,
    isImageDecoderSupported: false,
    stopAtErrors: false,
  });

  const loadPdfDocument = async (buffer) => {
    const firstAttemptTask = pdfjsLib.getDocument(
      getPdfDocumentOptions(new Uint8Array(buffer.slice(0))),
    );

    try {
      return await firstAttemptTask.promise;
    } catch (firstError) {
      console.warn("Primary PDF read failed, retrying once.", firstError);

      const secondAttemptTask = pdfjsLib.getDocument(
        getPdfDocumentOptions(new Uint8Array(buffer.slice(0))),
      );

      return secondAttemptTask.promise;
    }
  };

  const getReadablePdfError = (error) => {
    const errorName = error?.name || "";
    const errorMessage = error?.message || "";

    if (errorName === "PasswordException") {
      return "This PDF is password-protected and cannot be imported here.";
    }

    if (errorName === "InvalidPDFException") {
      return "This PDF appears to be invalid or corrupted and could not be read.";
    }

    if (
      errorMessage.includes("detached ArrayBuffer") ||
      errorMessage.includes("ArrayBuffer")
    ) {
      return "This PDF could not be processed safely in the browser. Please try a smaller file or a different PDF export.";
    }

    return "This PDF could not be read. It may be protected, corrupted, or incompatible with browser-based text extraction.";
  };

  const createPdfBytes = () => {
    if (!pdfDataRef.current) {
      return null;
    }

    return new Uint8Array(pdfDataRef.current.slice(0));
  };

  const parsePageSelection = (rawValue, totalPages) => {
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set();
    const segments = trimmedValue.split(",");

    for (const segment of segments) {
      const token = segment.trim();

      if (!token) {
        throw new Error("Use page numbers like 1, 3, 5-8.");
      }

      if (token.includes("-")) {
        const [startRaw, endRaw] = token.split("-").map((part) => part.trim());
        const start = Number(startRaw);
        const end = Number(endRaw);

        if (
          !Number.isInteger(start) ||
          !Number.isInteger(end) ||
          start <= 0 ||
          end <= 0
        ) {
          throw new Error("Page ranges must use positive whole numbers.");
        }

        if (start > end) {
          throw new Error(
            "Page ranges must go from a smaller page to a larger one.",
          );
        }

        for (let page = start; page <= end; page += 1) {
          pages.add(page);
        }
      } else {
        const page = Number(token);

        if (!Number.isInteger(page) || page <= 0) {
          throw new Error("Page numbers must be positive whole numbers.");
        }

        pages.add(page);
      }
    }

    const selectedPages = Array.from(pages).sort((a, b) => a - b);

    if (selectedPages.some((page) => page > totalPages)) {
      throw new Error("Some selected pages are outside the PDF page range.");
    }

    return selectedPages;
  };

  const extractTextFromPdfPages = async (selectedPages) => {
    const pdfBytes = createPdfBytes();

    if (!pdfBytes) {
      throw new Error("Upload a PDF before importing pages.");
    }

    const pdfDocument = await loadPdfDocument(pdfBytes.buffer);
    const pageTexts = [];

    for (const pageNumber of selectedPages) {
      const page = await pdfDocument.getPage(pageNumber);
      const textContent = await page.getTextContent();
      const normalizedText = buildParagraphTextFromPage(textContent);

      if (normalizedText) {
        pageTexts.push(normalizedText);
      }
    }

    return pageTexts.join("\n\n");
  };

  const handlePdfFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const isPdfFile =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");

    if (!isPdfFile) {
      setPdfError("Please choose a PDF file.");
      addToast("Please choose a PDF file.", "error-warning");
      event.target.value = "";
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const fileBuffer = await file.arrayBuffer();
      const pdfDocument = await loadPdfDocument(fileBuffer);

      pdfDataRef.current = fileBuffer.slice(0);
      setPdfFileName(file.name);
      setPdfPageCount(pdfDocument.numPages);
      setPdfPagesInput("");
      setPdfPanelOpen(true);
      addToast(`PDF ready: ${pdfDocument.numPages} pages detected.`, "success-info");
    } catch (error) {
      console.error("Failed to read PDF", error);
      pdfDataRef.current = null;
      setPdfFileName("");
      setPdfPageCount(0);
      setPdfPanelOpen(false);
      const message = getReadablePdfError(error);
      setPdfError(message);
      addToast(message, "error-warning");
    } finally {
      setPdfLoading(false);
      event.target.value = "";
    }
  };

  const handleImportPdf = async () => {
    if (!pdfDataRef.current || !pdfPageCount) {
      setPdfError("Upload a PDF before importing pages.");
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const selectedPages = parsePageSelection(pdfPagesInput, pdfPageCount);
      const extractedText = await extractTextFromPdfPages(selectedPages);

      if (!extractedText) {
        const emptyResultMessage =
          "No extractable text was found in the selected pages. This PDF may be scanned or image-based.";
        setPdfError(emptyResultMessage);
        addToast(emptyResultMessage, "error-warning");
        return;
      }

      const trimmedCurrentBody = currentText.trim();
      const nextBody = trimmedCurrentBody
        ? `${trimmedCurrentBody}\n\n${extractedText}`
        : extractedText;

      if (Number.isFinite(maxLength) && nextBody.length > maxLength) {
        throw new Error(
          "The extracted text is too long for a single lesson. Try importing fewer pages.",
        );
      }

      onImportText(nextBody);
      handleClearPdf();
      addToast("Selected PDF pages were added to the lesson body.", "success-info");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "PDF import failed. Please try again.";
      setPdfError(message);
      addToast(message, "error-warning");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClearPdf = () => {
    pdfDataRef.current = null;
    setPdfFileName("");
    setPdfPageCount(0);
    setPdfPagesInput("");
    setPdfPanelOpen(false);
    setPdfError("");
  };

  return (
    <div className={`pdfimport pdfimport-${placement}`}>
      <div className="pdfimport-actions">
        <input
          ref={pdfInputRef}
          type="file"
          accept=".pdf,application/pdf"
          onChange={handlePdfFileChange}
          className="pdfimport-input"
        />
        <Button
          className={
            triggerVariant === "icon"
              ? "study-button pdfimport-upload-button pdfimport-upload-button-icon"
              : "button-btn pdfimport-upload-button"
          }
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            pdfInputRef.current?.click();
          }}
          disabled={pdfLoading}
          title="Upload PDF"
          data-allow-navigation="true"
        >
          <Upload
            size={16}
            className={
              triggerVariant === "icon" ? "pdfimport-upload-icon" : ""
            }
          />
          {triggerVariant === "icon" ? (
            <span className="pdfimport-beta-badge pdfimport-beta-badge-icon">
              Beta
            </span>
          ) : (
            <>
              <span className="pdfimport-upload-label-desktop">
                {pdfLoading ? "Preparing PDF..." : "Upload PDF"}
              </span>
              <span className="pdfimport-upload-label-mobile">
                {pdfLoading ? "PDF..." : "PDF"}
              </span>
              <span className="pdfimport-beta-badge pdfimport-beta-badge-default">
                Beta
              </span>
            </>
          )}
        </Button>
      </div>

      {pdfPanelOpen && (
        <div className="pdfimport-panel">
          <div className="pdfimport-panel-header">
            <div className="pdfimport-file">
              <FileText size={18} />
              <div className="pdfimport-file-copy">
                <strong>{pdfFileName}</strong>
                <span>{pdfPageCount} pages detected</span>
              </div>
            </div>
            <Button
              type="button"
              className="button-btn button-btn-red pdfimport-clear"
              onClick={handleClearPdf}
            >
              Remove PDF
            </Button>
          </div>

          <div className="pdfimport-note">{PDF_IMPORT_MESSAGE}</div>

          <div className="pdfimport-controls">
            <div className="pdfimport-field">
              <label htmlFor="pdf-pages">Pages to import</label>
              <input
                type="text"
                id="pdf-pages"
                name="pdf-pages"
                value={pdfPagesInput}
                onChange={(event) => setPdfPagesInput(event.target.value)}
                placeholder="Leave empty, or use 3, 9, 12 or 1-5"
                autoComplete="off"
              />
              <span className="pdfimport-hint">
                Leave this empty to import the full PDF. Use commas (e.g. 3, 9,
                12) or ranges (e.g. 1-5) to limit large files.
              </span>
              {!pdfPagesInput.trim() && (
                <span className="pdfimport-selection-badge">All pages selected</span>
              )}
            </div>

            <Button
              className="button-btn button-btn-blue pdfimport-import-button"
              type="button"
              onClick={handleImportPdf}
              disabled={pdfLoading}
            >
              {pdfLoading ? "Importing..." : "Import selected pages"}
            </Button>
          </div>

          {pdfError && <div className="pdfimport-error">{pdfError}</div>}
        </div>
      )}
    </div>
  );
};

export default PdfImportPanel;
